import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormControl } from '@angular/forms';

import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';

import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';

import { PrescriptionPlanningStatus } from '@lib/prescriptions/data-access';
import { getAllowedPlanningStatuses } from '@lib/prescriptions/data-access';

@Component({
  selector: 'prescriptions-appointment-status-control',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatSelectTrigger,
    TranslatePipe,
    StatusComponent,
  ],
  template: `
    <mat-form-field>
      <mat-label>{{ "appointments.label.status" | translate }}</mat-label>
      <mat-select
        [formControl]="selectControl"
        [disabled]="disabled()"
        (selectionChange)="onSelectionChange($event.value)"
      >
        <mat-select-trigger>
          <shared-status
            [statusValue]="selectControl.value"
            [statusMessage]="'status.prescription.planning.' + selectControl.value | translate"
          ></shared-status>
        </mat-select-trigger>

        @for (status of allowedStatuses(); track status) {
          <mat-option [value]="status">
            <shared-status
              [statusValue]="status"
              [statusMessage]="'status.prescription.planning.' + status | translate"
            ></shared-status>
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppointmentStatusControlComponent),
      multi: true,
    },
  ],
})
export class AppointmentStatusControlComponent implements ControlValueAccessor {
  originalStatus = input.required<PrescriptionPlanningStatus>();

  protected readonly selectControl = new FormControl<PrescriptionPlanningStatus>(null);
  protected readonly disabled = signal<boolean>(false);

  protected readonly allowedStatuses = computed<PrescriptionPlanningStatus[]>(() =>
    getAllowedPlanningStatuses(this.originalStatus()),
  );

  private onChange: (value: PrescriptionPlanningStatus) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: PrescriptionPlanningStatus): void {
    this.selectControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: PrescriptionPlanningStatus) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onSelectionChange(value: PrescriptionPlanningStatus): void {
    this.onChange(value);
    this.onTouched();
  }
}
